import axios from 'axios';

const baseURL = `${process.env.GATSBY_STRAPI_FORM_URL}`;
const token = process.env.GATSBY_STRAPI_FORM_TOKEN;

const authInstance = axios.create({
  baseURL: baseURL,
  headers: {
    Authorization: `Bearer ${token}`,
    // "Content-Type": "multipart/form-data"
  },
});


export const postFileData = async (data) => {

  const res = await authInstance.post('upload', data)
  .then((res) => {
    // Success
    if (res.statusText === 'OK') {
      return {
        success: true,
        ...res.data,
      }
    }
    return { success: false }
  })
  .catch((error) => {
    // Failed
    if (error.response) {
      return {
        success: false,
        message: error.response.data,
      }
    } else {
      // Service error
    }
  })

  return res;

}

export const postFormData = async (data) => {

  const res = await authInstance.post('forms', data)
  .then((res) => {
    // Success
    if (res.statusText === 'OK') {
      return {
        success: true,
        ...res.data,
      }
    }
    return { success: false }
  })
  .catch((error) => {
    // Failed
    if (error.response) {
      return {
        success: false,
        message: error.response.data,
      }
    } else {
      // Service error
    }
  })

  return res;

}


export function nameValidation(name) {
  const ERROR = {
    name: "Invalid name",
  };

  //Check if name is a string
  if (typeof name !== "string") {
    return { valid: false, message: ERROR.name };
  }

  name = name.trim();

  //Minimum length
  if (name.length < 2) {
    return { valid: false, message: "Please enter your name" };
  }

  //Maximum length
  if (name.length > 60) {
    return { valid: false, message: "Name is too long" };
  }

  // Regex: only letters, spaces, apostrophes, dots, and hyphens
  const namePattern = /^[A-Za-z\s'.-]+$/;
  if (!namePattern.test(name)) {
    return { valid: false, message: "Enter a valid name" };
  }

  //Refine rules
  const hasLongConsonantRun = /[^aeiouAEIOU\s]{5,}/.test(name);
  const hasVowel = /[aeiouAEIOU]/.test(name);
  const validLength = name.length >= 2;

  if (!hasVowel || hasLongConsonantRun || !validLength) {
    return { valid: false, message: "Please enter a real name" };
  }

  // Detect mid-word uppercase (camelCase/random strings)
  // Real names only have uppercase at the START of a word
  const midWordUpperCase = (name.match(/(?<=[a-z])[A-Z]/g) || []).length;
  if (midWordUpperCase >= 2) {
    return { valid: false, message: "Please enter a valid name" };
  }

  // Consonant/vowel ratio check per word
  const parts = name.split(/\s+/).filter(Boolean);
  const vowels = /[aeiouAEIOU]/g;

  for (const part of parts) {
    // Each part must be between 2–20 chars
    if (part.length < 2 || part.length > 20) {
      return { valid: false, message: "Please enter a valid name" };
    }

    const vowelCount = (part.match(vowels) || []).length;
    const consonantCount = part.length - vowelCount;
    const ratio = consonantCount / part.length;

    // Real names rarely exceed 75% consonants
    if (ratio > 0.75) {
      return { valid: false, message: "Please enter a real name" };
    }
  }

  // Bigram uniqueness check (detects random character sequences)
  // naturally have high bigram uniqueness — threshold was too aggressive
  for (const part of parts) {
    const lowerPart = part.toLowerCase();
    const bigrams = new Set();
    for (let i = 0; i < lowerPart.length - 1; i++) {
      bigrams.add(lowerPart[i] + lowerPart[i + 1]);
    }
    const uniquenessRatio = bigrams.size / (lowerPart.length - 1);
    // ── Only flag as gibberish if VERY long AND almost all bigrams unique ──
    if (lowerPart.length > 12 && uniquenessRatio > 0.95) {
      return { valid: false, message: "Please enter a real name" };
    }
  }

  // Passed all checks
  return { valid: true, message: "Valid name" };
}

export function emailValidation(email) {
  const ERROR = {
    email: "Invalid email",
  };

  //Check if email is a string
  if (typeof email !== "string") {
    return { valid: false, message: ERROR.email };
  }

  email = email.trim().toLowerCase(); // ← normalise to catch duplicate cases like User@Gmail.com vs user@gmail.com

  //Minimum length
  if (email.length < 5) {
    return { valid: false, message: "Email is too short" };
  }

  //Maximum length
  if (email.length > 100) {
    return { valid: false, message: "Email is too long" };
  }

  //Basic format check (RFC 5322–like, simplified)
  const emailPattern = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  if (!emailPattern.test(email)) {
    return { valid: false, message: "Please enter a valid email address" };
  }

  //Optional refine checks
  // Prevent obvious fake-looking emails
  const [localPart, domain] = email.split("@");
  const tld = domain.split(".").pop();

  if (tld.length < 2) {
    return { valid: false, message: "Please enter a valid Email" };
  }

  // Reject emails with too many dots or special chars
  if (/\.\.|--|__/.test(email)) {
    return { valid: false, message: "Please enter a valid email address" };
  }

  // Reject if domain doesn’t contain at least one dot
  if (!domain.includes(".")) {
    return { valid: false, message: "Please enter a valid domain" };
  }

  // Block disposable/temporary email domains
  const disposableDomains = [
    "mailinator.com", "guerrillamail.com", "tempmail.com", "throwam.com",
    "yopmail.com", "sharklasers.com", "guerrillamailblock.com", "grr.la",
    "guerrillamail.info", "spam4.me", "trashmail.com", "dispostable.com",
    "fakeinbox.com", "mailnull.com", "spamgourmet.com", "trashmail.me",
    "maildrop.cc", "discard.email", "spamherelots.com", "spamhereplease.com",
    "tempr.email", "throwam.com", "33mail.com", "spamgourmet.net"
  ];
  if (disposableDomains.includes(domain)) {
    return { valid: false, message: "Please use a valid email address" };
  }

  // Block obviously fake local parts
  const blockedLocalParts = [
    "test", "fake", "noemail", "noreply", "no-reply", "donotreply",
    "admin", "null", "none", "example", "sample", "demo", "temp", "temporary"
  ];
  if (blockedLocalParts.includes(localPart)) {
    return { valid: false, message: "Please enter a real email address" };
  }

  // Block purely numeric local parts (e.g. 12345@gmail.com)
  if (/^\d+$/.test(localPart)) {
    return { valid: false, message: "Please enter a valid email address" };
  }

  // Block no vowels + short local part
  if (!/[aeiou]/.test(localPart) && localPart.length < 4) {
    return { valid: false, message: "Please enter a real email address" };
  }

  // Block excessive dot-splitting: ly.ri.cal.wa.v.e pattern
  // If local part has 4+ segments when split by dot and most are <= 2 chars → fake
  const dotSegments = localPart.split(".");
  if (dotSegments.length >= 4) {
    const shortSegments = dotSegments.filter((seg) => seg.length <= 2).length;
    const shortRatio = shortSegments / dotSegments.length;
    if (shortRatio >= 0.5) {
      return { valid: false, message: "Please enter a valid email address" };
    }
  }

  //Passed all checks
  return { valid: true, message: "Valid email" };
}